import { TARIF_TYPE, UNITE_TYPES } from "Achat/Constants";
import moment from "moment";

/**
* Utilitaire contenant des fonctions statiques pour la gestion des achats
* @category Utils
* @hideconstructor
*/
class AchatUtils {
  /**
    * Fonction permettant de récupérer la tranche de prix pour chaque quantité de service
    * @param {object} service Le service pour lequel on veut récupérer les tranches de prix
    * @returns {array} Tableau des tranches de prix avec les quantités et les prix
  */
  static getCurrentTranche(tarif, quantite) {
    // Si une seule tranche ou pas de quantité
    if ((tarif && tarif.tranches.length === 1)) {
      return tarif.tranches[0];
    }

    // Sinon recherche de la bonne tranche de tarif
    // On récupère la date de validité avec la valeur
    const dateValidite = (tarif.idTarifType === TARIF_TYPE.TEMPS && quantite) > 1
      ? moment().add(quantite * tarif.secondes, "second").format()
      : moment().format()


    // On filtre les tarifs qui sont dans la date / période de validité
    let tranchesFilter = [];
    tranchesFilter = tarif.tranches.filter(item => {
      // Recherche des tranches entre la date de début et fin

      if (moment(dateValidite).isBetween(item.dateDebut, item.dateFin)) {
        const periodeDeb = moment(moment(dateValidite).format("DD/MM/YYYY") + ' ' + item.periodeDebut, 'DD/MM/YYYY HH:mm').format();
        const periodeFin = moment(moment(dateValidite).format("DD/MM/YYYY") + ' ' + item.periodeFin, 'DD/MM/YYYY HH:mm').format();

        // Recherche dans la période
        return moment(dateValidite).isBetween(periodeDeb, periodeFin);
      }

      return false;
    });

    // on renvoie le bon item dans la liste
    let tranche = tranchesFilter.length === 1 || !quantite
      ? tranchesFilter[0]
      : tranchesFilter.find(item => (quantite <= item.uniteMax) && (quantite >= item.uniteMin))

    // Fallback uniquement si la quantité dépasse le max de toutes les tranches (ex. 25 ou 30 pour paliers 1,6,12,16,24)
    if (!tranche && quantite && tranchesFilter.length > 0) {
      const maxUnite = Math.max(...tranchesFilter.map(t => t.uniteMax));
      if (quantite > maxUnite) {
        const tranchesSorted = [...tranchesFilter].sort((a, b) => b.uniteMax - a.uniteMax);
        tranche = tranchesSorted[0];
      }
    }

    return tranche;
  };

  /**
   * Retourne la liste des quantités valides (définies par au moins une tranche) pour un tarif.
   * Utilisé pour faire sauter les quantités non définies dans le sélecteur (+/-).
   * @param {object} tarif Tarif avec tranches (même filtre date/période que getCurrentTranche)
   * @returns {number[]} Quantités valides triées, ex. [1, 6, 12, 16, 24]
   */
  static getValidQuantites(tarif) {
    if (!tarif?.tranches?.length) return [];
    if (tarif.tranches.length === 1) {
      const t = tarif.tranches[0];
      const min = t.uniteMin ?? 0;
      const max = t.uniteMax ?? min;
      const list = [];
      for (let q = min; q <= max; q++) list.push(q);
      return list;
    }
    const dateValidite = moment().format();
    const tranchesFilter = tarif.tranches.filter(item => {
      if (!moment(dateValidite).isBetween(item.dateDebut, item.dateFin)) return false;
      const periodeDeb = moment(moment(dateValidite).format("DD/MM/YYYY") + ' ' + item.periodeDebut, 'DD/MM/YYYY HH:mm').format();
      const periodeFin = moment(moment(dateValidite).format("DD/MM/YYYY") + ' ' + item.periodeFin, 'DD/MM/YYYY HH:mm').format();
      return moment(dateValidite).isBetween(periodeDeb, periodeFin);
    });
    const set = new Set();
    tranchesFilter.forEach(item => {
      const min = item.uniteMin ?? 0;
      const max = item.uniteMax ?? min;
      for (let q = min; q <= max; q++) set.add(q);
    });
    return Array.from(set).sort((a, b) => a - b);
  }

  //AYB 08.09.26 Filtre dateDebut/dateFin + periodeDebut/periodeFin (même logique que getCurrentTranche), sans palier de quantité.
  static isTrancheValide(item, dateValidite = moment()) {
    if (!item || item.actif === 0) return false;
    if (!moment(dateValidite).isBetween(item.dateDebut, item.dateFin)) return false;
    const periodeDeb = moment(moment(dateValidite).format("DD/MM/YYYY") + " " + item.periodeDebut, "DD/MM/YYYY HH:mm").format();
    const periodeFin = moment(moment(dateValidite).format("DD/MM/YYYY") + " " + item.periodeFin, "DD/MM/YYYY HH:mm").format();
    return moment(dateValidite).isBetween(periodeDeb, periodeFin);
  }

  //AYB 08.09.26 Tranches d'un tarif encore valides à la date/heure courante.
  static getTranchesValides(tarif) {
    if (!tarif?.tranches?.length || tarif.actif === 0) return [];
    const dateValidite = moment();
    return tarif.tranches.filter((item) => AchatUtils.isTrancheValide(item, dateValidite));
  }

  //AYB 08.09.26 Tous les tarifs "4" : 1 entrée liste par tarif, avec ses tranches valides date/période (1 ligne même si plusieurs paliers min/max).
  static getTranchesChoix(tarifsDuService) {
    if (!Array.isArray(tarifsDuService) || !tarifsDuService.length) return [];
    return tarifsDuService
      .filter((tarif) => String(tarif?.idTarifType) === TARIF_TYPE.CHOIX && tarif.actif !== 0)
      .map((tarif) => {
        const tranches = AchatUtils.getTranchesValides(tarif)
          .map((tranche) => ({ ...tranche, tarif }))
          .sort((a, b) => (a.uniteMin ?? 0) - (b.uniteMin ?? 0));
        if (!tranches.length) return null;
        return {
          idTarif: tarif.idTarif,
          tarif,
          tranches,
        };
      })
      .filter(Boolean);
  }

  //AYB 08.09.26 Tranche correspondant à la quantité (uniteMin/uniteMax). 1 tranche → celle-ci ; plusieurs → palier de la qté, sinon 1re.
  static getTrancheChoixForQuantite(tranchesChoix, quantite) {
    if (!tranchesChoix?.length) return null;
    if (tranchesChoix.length === 1 || !quantite) return tranchesChoix[0];
    const found = tranchesChoix.find((item) => {
      const min = item.uniteMin ?? 0;
      const max = item.uniteMax ?? min;
      return quantite >= min && quantite <= max;
    });
    if (found) return found;
    const maxUnite = Math.max(...tranchesChoix.map((t) => t.uniteMax ?? t.uniteMin ?? 0));
    if (quantite > maxUnite) {
      return [...tranchesChoix].sort((a, b) => (b.uniteMax ?? 0) - (a.uniteMax ?? 0))[0];
    }
    return tranchesChoix[0];
  }

  //AYB 08.09.26 Quantités couvertes par les tranches "4" déjà filtrées date/période (pour le +/-).
  static getValidQuantitesChoix(tranchesChoix) {
    const set = new Set();
    (tranchesChoix || []).forEach((item) => {
      const min = item.uniteMin ?? 0;
      const max = item.uniteMax ?? min;
      for (let q = min; q <= max; q++) set.add(q);
    });
    return Array.from(set).sort((a, b) => a - b);
  }

  //AYB 09.09.26 Traduit "1 heure" / "3 heures" selon la langue (type "1" et "4").
  static translateLibelleType(libelleType, t, idUniteType) {
    if (!libelleType) return "";
    if (idUniteType === UNITE_TYPES.EMPLACEMENT_24H) {
      return t("achat.emplacement24h");
    }
    const parts = String(libelleType).trim().split(/\s+/);
    const last = (parts[parts.length - 1] || "").toLowerCase();
    const unitKey = {
      heure: "achat.hour",
      hour: "achat.hour",
      hora: "achat.hour",
      ora: "achat.hour",
      stunde: "achat.hour",
      uur: "achat.hour",
      heures: "achat.hours",
      hours: "achat.hours",
      horas: "achat.hours",
      ore: "achat.hours",
      stunden: "achat.hours",
      minute: "achat.minute",
      minuto: "achat.minute",
      minuut: "achat.minute",
      minutes: "achat.minutes",
      minutos: "achat.minutes",
      minuti: "achat.minutes",
      minuten: "achat.minutes",
      jour: "achat.day",
      day: "achat.day",
      día: "achat.day",
      dia: "achat.day",
      dag: "achat.day",
      giorno: "achat.day",
      tag: "achat.day",
      jours: "achat.days",
      days: "achat.days",
      días: "achat.days",
      dias: "achat.days",
      dagen: "achat.days",
      giorni: "achat.days",
      tage: "achat.days",
    }[last];
    if (!unitKey) return libelleType;
    const prefix = parts.slice(0, -1).join(" ");
    return prefix ? `${prefix} ${t(unitKey)}` : t(unitKey);
  }

  //AYB 10.09.26 Nb d'unités taxe = (qté parent × secondes unité parent) / secondes unité taxe.
  // Ex. parent 48h (172800) / taxe Personne 24H (86400) = 2. Type "1" : 2 × 24h = (2 × 86400) / 86400 = 2.
  static getNbUnitesTaxe(serviceParent, tarifTaxe) {
    const parentQte = Number(serviceParent?.quantite) || 0;
    const parentSecondes = Number(serviceParent?.tarif?.secondes) || 0;
    const taxeSecondes = Number(tarifTaxe?.secondes) || 0;
    if (!taxeSecondes || !parentSecondes) {
      return parentQte;
    }
    return (parentQte * parentSecondes) / taxeSecondes;
  }

  /**
   * Fonction qui renvoie les numéros à la fin d'une chaîne. Utile pour récupérer les numéros de service
   * @param {string} chaine
   * @returns {string}
   */
  static getStringEndingNumbers(chaine) {
    const regex = /(\d+)$/;

    // Recherche des correspondances
    const match = chaine.match(regex);

    // Retourne la chaîne de chiffres trouvée ou null si aucun nombre n'est trouvé
    return match ? match[1] : null;
  }

  /**
    * Fonction permettant de récupérer la tranche de prix pour chaque quantité de service
    * @param {object} service Le service pour lequel on veut récupérer les tranches de prix
    * @returns {array} Tableau des tranches de prix avec les quantités et les prix
  */
  static getTranches(service) {
    //AYB 08.09.26 Type "4" : une ligne = la tranche de la quantité courante (pas un forfait par tranche).
    if (String(service?.tarif?.idTarifType) === TARIF_TYPE.CHOIX && service.actualTranche) {
      return [{
        idTranche: service.actualTranche.idTranche,
        quantite: service.quantite || 1,
        prix: service.actualTranche.prix,
      }];
    }

    let tranches = [];

    for (let i = 1; i <= service.quantite; i++) {
      const actualTranche = getCurrentTranche(service.tarif, i);
      if (!actualTranche) continue; // ne pas compter les quantités non définies dans une tranche

      if (!tranches.find(item => item.idTranche === actualTranche.idTranche)) {
        tranches.push({
          idTranche: actualTranche.idTranche,
          quantite: 1,
          prix: actualTranche.prix,
        });
      } else {
        const index = tranches.findIndex(item => item.idTranche === actualTranche.idTranche);
        tranches[index].quantite = tranches[index].quantite + 1;
      }
    }
    return tranches;
  }
}

export const getCurrentTranche = AchatUtils.getCurrentTranche;
export const getStringEndingNumbers = AchatUtils.getStringEndingNumbers;
export const getTrancheChoixForQuantite = AchatUtils.getTrancheChoixForQuantite;
export const getTranches = AchatUtils.getTranches;
export const translateLibelleType = AchatUtils.translateLibelleType;
export const getTranchesChoix = AchatUtils.getTranchesChoix;
export const getTranchesValides = AchatUtils.getTranchesValides;
export const getValidQuantites = AchatUtils.getValidQuantites;
export const getValidQuantitesChoix = AchatUtils.getValidQuantitesChoix;
export const getNbUnitesTaxe = AchatUtils.getNbUnitesTaxe;
